import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { fetchSubscriptions, updateSubscription } from '@/lib/admin/subscriptions';
import { cn } from '@/lib/cn';

const HEALTH_STYLES = {
  green: 'bg-emerald-100 text-emerald-700',
  yellow: 'bg-amber-100 text-amber-700',
  red: 'bg-rose-100 text-rose-700',
} as const;

function bytesToMb(bytes?: number | null): string {
  if (bytes === null || bytes === undefined) return '';
  return String(Math.round((bytes / (1024 * 1024)) * 100) / 100);
}

function mbToBytes(mb: string): number | null {
  const v = parseFloat(mb);
  if (!Number.isFinite(v) || v <= 0) return null;
  return Math.round(v * 1024 * 1024);
}

export default function SubscriptionsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const plan = searchParams.get('plan') ?? '';

  const { data } = useQuery({
    queryKey: ['admin', 'subscriptions', { status, plan }],
    queryFn: () => fetchSubscriptions({ status: status || undefined, plan: plan || undefined }),
  });

  const [editing, setEditing] = useState<Record<string, string>>({});

  const updateMut = useMutation({
    mutationFn: ({ id, bytes }: { id: string; bytes: number | null }) =>
      updateSubscription(id, { docUploadMaxBytesOverride: bytes }),
    onSuccess: () => {
      toast.success(t('admin.subscriptions.uploadLimitSaved'));
      qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
      qc.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const items = data ?? [];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">{t('admin.nav.subscriptions')}</h1>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex flex-wrap gap-3">
        <select className="rounded-lg border border-gray-200 px-3 py-2 text-sm" value={plan} onChange={(e) => setParam('plan', e.target.value)}>
          <option value="">{t('admin.filters.allPlans')}</option>
          {['free', 'starter', 'professional', 'enterprise'].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="rounded-lg border border-gray-200 px-3 py-2 text-sm" value={status} onChange={(e) => setParam('status', e.target.value)}>
          <option value="">{t('admin.filters.allStatuses')}</option>
          {['active', 'trialing', 'past_due', 'canceled', 'incomplete', 'unpaid'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2">{t('admin.columns.user')}</th>
              <th className="px-4 py-2">{t('admin.columns.plan')}</th>
              <th className="px-4 py-2">{t('admin.columns.status')}</th>
              <th className="px-4 py-2">{t('admin.columns.trialEnd')}</th>
              <th className="px-4 py-2">{t('admin.columns.periodEnd')}</th>
              <th className="px-4 py-2">{t('admin.columns.mrr')}</th>
              <th className="px-4 py-2">{t('admin.columns.health')}</th>
              <th className="px-4 py-2">{t('admin.columns.uploadLimit')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">{t('common.noResults')}</td></tr>
            )}
            {items.map((s) => {
              const defaultMb = bytesToMb(s.docUploadMaxBytesDefault);
              const overrideMb = bytesToMb(s.docUploadMaxBytesOverride);
              const inputValue = editing[s.id] ?? overrideMb;
              const placeholder = defaultMb ? `${defaultMb} (default)` : '';
              return (
                <tr key={s.id}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900">{s.user.email}</div>
                    {s.organization && <div className="text-xs text-gray-500">{s.organization.name}</div>}
                  </td>
                  <td className="px-4 py-2 capitalize">{s.plan}</td>
                  <td className="px-4 py-2 capitalize">{s.status}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">{s.trialEndsAt ? new Date(s.trialEndsAt).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">{s.currentPeriodEnd ? new Date(s.currentPeriodEnd).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-2 font-medium">€{Number(s.mrrContribution || 0).toFixed(2)}</td>
                  <td className="px-4 py-2"><span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold capitalize', HEALTH_STYLES[s.health])}>{s.health}</span></td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={inputValue}
                        placeholder={placeholder}
                        onChange={(e) => setEditing({ ...editing, [s.id]: e.target.value })}
                        className="w-20 rounded-lg border border-gray-200 px-2 py-1 text-xs"
                      />
                      <span className="text-xs text-gray-500">MB</span>
                      <button
                        type="button"
                        disabled={updateMut.isPending}
                        onClick={() => {
                          const raw = editing[s.id] ?? overrideMb;
                          const bytes = raw === '' ? null : mbToBytes(raw);
                          if (raw !== '' && bytes === null) {
                            toast.error(t('admin.subscriptions.uploadLimitInvalid'));
                            return;
                          }
                          updateMut.mutate({ id: s.id, bytes });
                        }}
                        className="rounded-lg bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {t('common.save')}
                      </button>
                      {s.docUploadMaxBytesOverride && (
                        <button
                          type="button"
                          disabled={updateMut.isPending}
                          onClick={() => {
                            setEditing({ ...editing, [s.id]: '' });
                            updateMut.mutate({ id: s.id, bytes: null });
                          }}
                          className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                          title={t('admin.subscriptions.uploadLimitReset')}
                        >
                          ↺
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
